import { useId, useState } from 'react'

import type { TimetableElement } from '../../api/connections'

/**
 * Choosing one class out of what the other system offers.
 *
 * Shared by the form that sets a connection up and the panel that changes it afterwards, because it
 * is the same choice made twice and a second copy would be the one that stops matching.
 *
 * A **search field over a list**, not a drop-down: forty classes is more than a phone can usefully
 * show in a native select, a select cannot be searched by typing more than one letter, and the names
 * are all alike enough (`R101`, `R102`, `A101`) that scrolling past the right one is the ordinary
 * outcome. Typing two characters is the fast path; the list under it is the discoverable one.
 *
 * The rows are chips because here they genuinely are what a chip is: a set of alternatives standing
 * side by side, one of which is pressed.
 */
export interface ClassPickerProps {
  readonly choices: readonly TimetableElement[]
  readonly chosen: TimetableElement | null
  readonly onChoose: (one: TimetableElement) => void
}

export function ClassPicker({ choices, chosen, onChoose }: ClassPickerProps) {
  const base = useId()
  const [filter, setFilter] = useState('')

  const wanted = filter.trim().toLowerCase()
  const shown =
    wanted === '' ? choices : choices.filter((one) => one.name.toLowerCase().includes(wanted))

  return (
    <>
      <div className="field">
        <label className="field__label" htmlFor={`${base}-filter`}>
          Find your class
        </label>
        <input
          id={`${base}-filter`}
          className="field__control"
          type="search"
          value={filter}
          placeholder="R101, Rechtspflege…"
          onChange={(event) => {
            setFilter(event.target.value)
          }}
        />
      </div>
      <ul className="source__choices">
        {shown.map((one) => (
          <li key={one.id}>
            <button
              type="button"
              className="chip"
              aria-pressed={chosen?.id === one.id}
              onClick={() => {
                onChoose(one)
              }}
            >
              {one.name}
            </button>
          </li>
        ))}
        {shown.length === 0 && <li className="source__aside">Nothing there is called that.</li>}
      </ul>
    </>
  )
}
